import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  rsvpResponses,
  type EventParticipantView,
  type TrainingPlanView,
  type EventView,
  type ParticipantRefreshView,
  type RsvpResponse,
  type SeriesEditScope,
  type SupportedLocale,
  type UpdateEventRequest,
} from '@stm/contracts';
import { translate } from '@stm/i18n';
import { ApiRequestError, apiRequest } from './api';
import { athleteDisplayName, formatCalendarDate } from './athletes';
import { AttendanceSheet } from './AttendanceSheet';
import { AudiencePicker } from './AudiencePicker';
import {
  answerCounts,
  audienceOf,
  eventStatusKeys,
  eventTypeKeys,
  formatEventWhen,
  isEmptySelection,
  rsvpKeys,
  sameSelection,
  selectionOfAudience,
  type AudienceSelection,
} from './scheduling';
import { SeriesPanel } from './SeriesPanel';

export interface EventDetailProps {
  locale: SupportedLocale;
  /** `/api/v1/organizations/{id}` */
  base: string;
  csrfToken: string;
  eventId: string;
  /** Whether the person may change, publish and cancel events; the server enforces it as well. */
  canManage: boolean;
  /** Whether the person may record attendance and correct answers. */
  canRecord: boolean;
  /** Whether the person may read training plans and so choose one for the event. */
  canAttachPlan: boolean;
  onChanged: () => void;
  onClose: () => void;
}

type ErrorKey = ApiRequestError['messageKey'];

interface Failure {
  key: ErrorKey;
  code: string;
}

function failureOf(error: unknown): Failure {
  return error instanceof ApiRequestError
    ? { key: error.messageKey, code: error.code }
    : { key: 'error.unexpected', code: 'UNEXPECTED' };
}

export interface EditForm {
  title: string;
  date: string;
  startTime: string;
  endTime: string;
  location: string;
  audience: AudienceSelection;
  scope: SeriesEditScope;
}

export function editFormOf(event: EventView): EditForm {
  return {
    title: event.title,
    date: event.date,
    startTime: event.startTime,
    endTime: event.endTime,
    location: event.location ?? '',
    audience: selectionOfAudience(event.audience).selection,
    scope: 'this',
  };
}

/** Only what changed is sent, so a concurrent edit of another field survives. */
export function editChanges(
  event: EventView,
  form: EditForm,
): Omit<UpdateEventRequest, 'confirmAthleteOverlaps'> {
  const changes: Omit<UpdateEventRequest, 'confirmAthleteOverlaps'> = { scope: form.scope };
  if (form.title.trim() !== event.title) changes.title = form.title.trim();
  // The date of a whole series is fixed by its rule; only a single occurrence can move to another day.
  if (form.scope === 'this' && form.date !== event.date) changes.date = form.date;
  if (form.startTime !== event.startTime) changes.startTime = form.startTime;
  if (form.endTime !== event.endTime) changes.endTime = form.endTime;
  if (form.location.trim() !== (event.location ?? '')) {
    changes.location = form.location.trim() === '' ? null : form.location.trim();
  }
  if (!sameSelection(form.audience, selectionOfAudience(event.audience).selection)) {
    changes.audience = audienceOf(form.audience);
  }
  return changes;
}

interface ParticipantLineProps {
  locale: SupportedLocale;
  participant: EventParticipantView;
  canCorrect: boolean;
  onCorrect: (
    participant: EventParticipantView,
    response: RsvpResponse,
    reason: string,
  ) => Promise<void>;
}

function ParticipantLine({
  locale,
  participant,
  canCorrect,
  onCorrect,
}: ParticipantLineProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [response, setResponse] = useState<RsvpResponse>('yes');
  const [reason, setReason] = useState('');
  return (
    <Box component="li" sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
      <Typography sx={{ minWidth: 180 }}>{athleteDisplayName(participant, locale)}</Typography>
      <Chip
        size="small"
        variant="outlined"
        label={translate(
          locale,
          participant.rsvp === null ? 'calendar.rsvp.none' : rsvpKeys[participant.rsvp.response],
        )}
      />
      {participant.rsvp?.correctionReason != null && (
        <Typography variant="body2" color="text.secondary">
          {translate(locale, 'calendar.rsvp.corrected')}: {participant.rsvp.correctionReason}
        </Typography>
      )}
      {canCorrect && (
        <Button
          size="small"
          onClick={() => {
            setOpen((current) => !current);
          }}
        >
          {translate(locale, 'calendar.rsvp.correct')}
        </Button>
      )}
      {open && (
        <Box
          component="form"
          sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: '100%', pl: { sm: 2 } }}
          onSubmit={(submitted) => {
            submitted.preventDefault();
            void onCorrect(participant, response, reason.trim()).then(() => {
              setOpen(false);
              setReason('');
            });
          }}
        >
          <TextField
            select
            size="small"
            sx={{ minWidth: 120 }}
            label={translate(locale, 'calendar.family.answer')}
            value={response}
            onChange={(changed) => {
              setResponse(changed.target.value as RsvpResponse);
            }}
          >
            {rsvpResponses.map((candidate) => (
              <MenuItem key={candidate} value={candidate}>
                {translate(locale, rsvpKeys[candidate])}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            sx={{ minWidth: 260, flexGrow: 1 }}
            label={translate(locale, 'calendar.rsvp.reason')}
            value={reason}
            onChange={(changed) => {
              setReason(changed.target.value);
            }}
          />
          <Button type="submit" variant="contained" size="small">
            {translate(locale, 'calendar.rsvp.save')}
          </Button>
        </Box>
      )}
    </Box>
  );
}

export function EventDetail({
  locale,
  base,
  csrfToken,
  eventId,
  canManage,
  canRecord,
  canAttachPlan,
  onChanged,
  onClose,
}: EventDetailProps): React.JSX.Element {
  const eventPath = `${base}/events/${eventId}`;
  const [event, setEvent] = useState<EventView | null>(null);
  const [participants, setParticipants] = useState<EventParticipantView[]>([]);
  const [failure, setFailure] = useState<Failure | null>(null);
  const [mode, setMode] = useState<'view' | 'edit' | 'cancel'>('view');
  const [form, setForm] = useState<EditForm | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelFuture, setCancelFuture] = useState(false);
  const [notifyParticipants, setNotifyParticipants] = useState(true);
  const [refreshPlan, setRefreshPlan] = useState<ParticipantRefreshView | null>(null);
  const [saved, setSaved] = useState(false);
  const [plans, setPlans] = useState<TrainingPlanView[]>([]);
  const [chosenPlan, setChosenPlan] = useState('');
  const [planOverride, setPlanOverride] = useState('');

  const load = useCallback(async () => {
    try {
      const loaded = await apiRequest<EventView>(eventPath);
      setEvent(loaded);
      setChosenPlan(loaded.trainingPlanVersionId ?? '');
      setParticipants(await apiRequest<EventParticipantView[]>(`${eventPath}/participants`));
    } catch (error) {
      setFailure(failureOf(error));
    }
  }, [eventPath]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!canAttachPlan) return;
    apiRequest<TrainingPlanView[]>(`${base}/training-plans`).then(setPlans, () => {
      setPlans([]);
    });
  }, [base, canAttachPlan]);

  async function run(work: () => Promise<void>): Promise<void> {
    setFailure(null);
    setSaved(false);
    try {
      await work();
      setSaved(true);
      onChanged();
    } catch (error) {
      setFailure(failureOf(error));
    }
    // Reload either way: after a conflict the page must show the event's current state.
    await load();
  }

  async function publish(confirmAthleteOverlaps: boolean): Promise<void> {
    if (event === null) return;
    await run(async () => {
      await apiRequest<EventView>(`${eventPath}/publish`, {
        method: 'POST',
        body: { confirmAthleteOverlaps },
        csrfToken,
        ifMatchVersion: event.version,
      });
    });
  }

  async function complete(): Promise<void> {
    if (event === null) return;
    await run(async () => {
      await apiRequest<EventView>(`${eventPath}/complete`, {
        method: 'POST',
        body: {},
        csrfToken,
        ifMatchVersion: event.version,
      });
    });
  }

  async function cancel(): Promise<void> {
    if (event === null) return;
    await run(async () => {
      await apiRequest<EventView>(`${eventPath}/cancel`, {
        method: 'POST',
        body: {
          reason: cancelReason.trim(),
          scope: cancelFuture ? 'future' : 'this',
          notifyParticipants,
        },
        csrfToken,
        ifMatchVersion: event.version,
      });
      setMode('view');
      setCancelReason('');
    });
  }

  async function restore(confirmAthleteOverlaps: boolean): Promise<void> {
    if (event === null) return;
    await run(async () => {
      await apiRequest<EventView>(`${eventPath}/restore`, {
        method: 'POST',
        body: { notifyParticipants, confirmAthleteOverlaps },
        csrfToken,
        ifMatchVersion: event.version,
      });
      setMode('view');
    });
  }

  async function saveEdit(confirmAthleteOverlaps: boolean): Promise<void> {
    if (event === null || form === null) return;
    await run(async () => {
      await apiRequest<EventView>(eventPath, {
        method: 'PATCH',
        body: { ...editChanges(event, form), confirmAthleteOverlaps },
        csrfToken,
        ifMatchVersion: event.version,
      });
      setMode('view');
    });
  }

  async function attachPlan(): Promise<void> {
    if (event === null) return;
    await run(async () => {
      await apiRequest<EventView>(`${eventPath}/plan`, {
        method: 'PUT',
        body: {
          trainingPlanVersionId: chosenPlan === '' ? null : chosenPlan,
          overrideReason: planOverride.trim() === '' ? null : planOverride.trim(),
        },
        csrfToken,
        ifMatchVersion: event.version,
      });
    });
  }

  async function previewRefresh(): Promise<void> {
    setFailure(null);
    try {
      setRefreshPlan(
        await apiRequest<ParticipantRefreshView>(`${eventPath}/participants/refresh-preview`),
      );
    } catch (error) {
      setFailure(failureOf(error));
    }
  }

  async function applyRefresh(): Promise<void> {
    await run(async () => {
      await apiRequest<ParticipantRefreshView>(`${eventPath}/participants/refresh`, {
        method: 'POST',
        body: {},
        csrfToken,
      });
      setRefreshPlan(null);
    });
  }

  async function correct(
    participant: EventParticipantView,
    response: RsvpResponse,
    reason: string,
  ): Promise<void> {
    await run(async () => {
      await apiRequest(`${eventPath}/rsvp`, {
        method: 'PUT',
        body: { athleteId: participant.athleteId, response, ...(reason === '' ? {} : { reason }) },
        csrfToken,
      });
    });
  }

  if (event === null) {
    return (
      <Paper variant="outlined" sx={{ padding: 2, mt: 1 }}>
        {failure === null ? (
          <Typography>{translate(locale, 'calendar.loading')}</Typography>
        ) : (
          <Alert severity="error">{translate(locale, failure.key)}</Alert>
        )}
        <Button onClick={onClose}>{translate(locale, 'calendar.detail.close')}</Button>
      </Paper>
    );
  }

  const counts = answerCounts(participants);
  const started = new Date(event.startsAt).getTime() <= Date.now();
  const open = event.status === 'draft' || event.status === 'published';
  const overlapPending = failure?.code === 'ATHLETE_OVERLAP';

  return (
    <Paper
      variant="outlined"
      sx={{ padding: 2, mt: 1 }}
      component="section"
      aria-label={event.title}
    >
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant="h6" component="h3">
          {event.title}
        </Typography>
        <Chip size="small" label={translate(locale, eventTypeKeys[event.eventType])} />
        <Chip
          size="small"
          color={
            event.status === 'published'
              ? 'success'
              : event.status === 'cancelled'
                ? 'error'
                : 'default'
          }
          label={translate(locale, eventStatusKeys[event.status])}
        />
        <Button size="small" onClick={onClose} sx={{ ml: 'auto' }}>
          {translate(locale, 'calendar.detail.close')}
        </Button>
      </Box>
      <Typography>{formatEventWhen(event, locale)}</Typography>
      {event.resourceName !== null && (
        <Typography color="text.secondary">
          {translate(locale, 'calendar.detail.resource')}: {event.resourceName}
        </Typography>
      )}
      {event.location !== null && <Typography color="text.secondary">{event.location}</Typography>}
      {event.arrivalAt !== null && (
        <Typography color="text.secondary">
          {translate(locale, 'calendar.detail.arrival')}:{' '}
          {new Intl.DateTimeFormat(locale, {
            timeZone: event.timezone,
            hour: '2-digit',
            minute: '2-digit',
          }).format(new Date(event.arrivalAt))}
        </Typography>
      )}
      {event.capacity !== null && (
        <Typography color="text.secondary">
          {translate(locale, 'calendar.detail.capacity')}: {event.capacity}
        </Typography>
      )}
      <Typography color="text.secondary">
        {translate(locale, 'calendar.detail.deadline')}:{' '}
        {new Intl.DateTimeFormat(locale, {
          timeZone: event.timezone,
          dateStyle: 'medium',
          timeStyle: 'short',
        }).format(new Date(event.rsvpDeadlineAt))}
      </Typography>
      {event.seriesId !== null && (
        <Typography variant="body2" color="text.secondary">
          {translate(locale, 'calendar.detail.series')}{' '}
          {event.isException ? translate(locale, 'calendar.detail.exception') : ''}
        </Typography>
      )}
      {event.status === 'cancelled' && event.cancelReason !== null && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {translate(locale, 'calendar.detail.cancelReason')}: {event.cancelReason}
        </Alert>
      )}
      {event.resourceId !== null && !event.resourceReserved && open && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {translate(locale, 'calendar.detail.notReserved')}
        </Alert>
      )}
      {failure !== null && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {translate(locale, failure.key)}
        </Alert>
      )}
      {saved && failure === null && (
        <Alert severity="success" sx={{ mt: 1 }}>
          {translate(locale, 'calendar.saved')}
        </Alert>
      )}

      {canManage && open && mode === 'view' && (
        <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
          {event.status === 'draft' && (
            <Button
              variant="contained"
              sx={{ minHeight: 44 }}
              onClick={() => {
                void publish(false);
              }}
            >
              {translate(locale, 'calendar.action.publish')}
            </Button>
          )}
          {event.status === 'draft' && overlapPending && (
            <Button
              variant="outlined"
              color="warning"
              sx={{ minHeight: 44 }}
              onClick={() => {
                void publish(true);
              }}
            >
              {translate(locale, 'calendar.action.publishAnyway')}
            </Button>
          )}
          {event.status === 'published' && started && (
            <Button
              variant="outlined"
              sx={{ minHeight: 44 }}
              onClick={() => {
                void complete();
              }}
            >
              {translate(locale, 'calendar.action.complete')}
            </Button>
          )}
          <Button
            variant="outlined"
            sx={{ minHeight: 44 }}
            onClick={() => {
              setForm(editFormOf(event));
              setMode('edit');
            }}
          >
            {translate(locale, 'calendar.action.edit')}
          </Button>
          <Button
            variant="outlined"
            color="error"
            sx={{ minHeight: 44 }}
            onClick={() => {
              setMode('cancel');
            }}
          >
            {translate(locale, 'calendar.action.cancel')}
          </Button>
        </Stack>
      )}

      {canManage && event.status === 'cancelled' && !started && mode === 'view' && (
        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          {event.publishedAt !== null && (
            <FormControlLabel
              label={translate(locale, 'calendar.series.notify')}
              control={
                <Checkbox
                  checked={notifyParticipants}
                  onChange={(changed) => {
                    setNotifyParticipants(changed.target.checked);
                  }}
                />
              }
            />
          )}
          <Button
            variant="contained"
            sx={{ minHeight: 44 }}
            onClick={() => {
              void restore(false);
            }}
          >
            {translate(locale, 'calendar.action.restore')}
          </Button>
          {overlapPending && (
            <Button
              variant="outlined"
              color="warning"
              sx={{ minHeight: 44 }}
              onClick={() => {
                void restore(true);
              }}
            >
              {translate(locale, 'calendar.series.restoreAnyway')}
            </Button>
          )}
        </Box>
      )}

      {mode === 'edit' && form !== null && (
        <Box
          component="form"
          sx={{ mt: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}
          onSubmit={(submitted) => {
            submitted.preventDefault();
            void saveEdit(false);
          }}
        >
          <TextField
            size="small"
            required
            sx={{ minWidth: 240 }}
            label={translate(locale, 'calendar.field.title')}
            value={form.title}
            onChange={(changed) => {
              setForm({ ...form, title: changed.target.value });
            }}
          />
          <TextField
            size="small"
            type="date"
            disabled={form.scope === 'future'}
            label={translate(locale, 'calendar.field.date')}
            value={form.date}
            onChange={(changed) => {
              setForm({ ...form, date: changed.target.value });
            }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            size="small"
            type="time"
            label={translate(locale, 'calendar.field.startTime')}
            value={form.startTime}
            onChange={(changed) => {
              setForm({ ...form, startTime: changed.target.value });
            }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            size="small"
            type="time"
            label={translate(locale, 'calendar.field.endTime')}
            value={form.endTime}
            onChange={(changed) => {
              setForm({ ...form, endTime: changed.target.value });
            }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            size="small"
            sx={{ minWidth: 220 }}
            label={translate(locale, 'calendar.field.location')}
            value={form.location}
            onChange={(changed) => {
              setForm({ ...form, location: changed.target.value });
            }}
          />
          <Box sx={{ width: '100%' }}>
            <AudiencePicker
              locale={locale}
              base={base}
              departmentId={event.departmentId}
              selection={form.audience}
              knownNames={selectionOfAudience(event.audience).names}
              onChange={(audience) => {
                setForm({ ...form, audience });
              }}
            />
            {event.status === 'published' && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {translate(locale, 'calendar.audience.publishedHint')}
              </Typography>
            )}
          </Box>
          {event.seriesId !== null && (
            <FormControl sx={{ width: '100%' }}>
              <FormLabel id={`scope-${event.id}`}>
                {translate(locale, 'calendar.scope.title')}
              </FormLabel>
              <RadioGroup
                aria-labelledby={`scope-${event.id}`}
                value={form.scope}
                onChange={(changed) => {
                  setForm({ ...form, scope: changed.target.value as SeriesEditScope });
                }}
              >
                <FormControlLabel
                  value="this"
                  control={<Radio />}
                  label={translate(locale, 'calendar.scope.this')}
                />
                <FormControlLabel
                  value="future"
                  control={<Radio />}
                  label={translate(locale, 'calendar.scope.future')}
                />
              </RadioGroup>
            </FormControl>
          )}
          <Stack direction="row" spacing={1} sx={{ width: '100%', flexWrap: 'wrap', gap: 1 }}>
            <Button
              type="submit"
              variant="contained"
              sx={{ minHeight: 44 }}
              disabled={event.status === 'published' && isEmptySelection(form.audience)}
            >
              {translate(locale, 'calendar.action.save')}
            </Button>
            {overlapPending && (
              <Button
                variant="outlined"
                color="warning"
                sx={{ minHeight: 44 }}
                onClick={() => {
                  void saveEdit(true);
                }}
              >
                {translate(locale, 'calendar.action.save')} —{' '}
                {translate(locale, 'calendar.overlap.notice')}
              </Button>
            )}
            <Button
              sx={{ minHeight: 44 }}
              onClick={() => {
                setMode('view');
                setFailure(null);
              }}
            >
              {translate(locale, 'calendar.action.discard')}
            </Button>
          </Stack>
        </Box>
      )}

      {mode === 'cancel' && (
        <Box
          component="form"
          sx={{ mt: 2, display: 'flex', gap: 1.5, flexDirection: 'column' }}
          onSubmit={(submitted) => {
            submitted.preventDefault();
            void cancel();
          }}
        >
          <TextField
            size="small"
            required
            label={translate(locale, 'calendar.action.cancelReason')}
            value={cancelReason}
            onChange={(changed) => {
              setCancelReason(changed.target.value);
            }}
          />
          {event.status === 'published' && (
            <FormControlLabel
              label={translate(locale, 'calendar.series.notify')}
              control={
                <Checkbox
                  checked={notifyParticipants}
                  onChange={(changed) => {
                    setNotifyParticipants(changed.target.checked);
                  }}
                />
              }
            />
          )}
          {event.seriesId !== null && (
            <FormControlLabel
              label={translate(locale, 'calendar.action.cancelFuture')}
              control={
                <Checkbox
                  checked={cancelFuture}
                  onChange={(changed) => {
                    setCancelFuture(changed.target.checked);
                  }}
                />
              }
            />
          )}
          <Stack direction="row" spacing={1}>
            <Button
              type="submit"
              variant="contained"
              color="error"
              sx={{ minHeight: 44 }}
              disabled={cancelReason.trim() === ''}
            >
              {translate(locale, 'calendar.action.cancelConfirm')}
            </Button>
            <Button
              sx={{ minHeight: 44 }}
              onClick={() => {
                setMode('view');
              }}
            >
              {translate(locale, 'calendar.action.discard')}
            </Button>
          </Stack>
        </Box>
      )}

      {canManage && event.seriesId !== null && (
        <SeriesPanel
          key={event.seriesId}
          locale={locale}
          base={base}
          csrfToken={csrfToken}
          seriesId={event.seriesId}
          currentEventId={event.id}
          onChanged={() => {
            onChanged();
            void load();
          }}
        />
      )}

      {canAttachPlan && canManage && open && (
        <Box
          component="form"
          aria-label={translate(locale, 'training.attach.title')}
          sx={{ mt: 3, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}
          onSubmit={(submitted) => {
            submitted.preventDefault();
            void attachPlan();
          }}
        >
          <TextField
            select
            size="small"
            sx={{ minWidth: 260 }}
            label={translate(locale, 'training.attach.title')}
            value={chosenPlan}
            onChange={(changed) => {
              setChosenPlan(changed.target.value);
            }}
          >
            <MenuItem value="">{translate(locale, 'training.attach.none')}</MenuItem>
            {plans
              .filter((plan) => plan.departmentId === event.departmentId)
              .flatMap((plan) =>
                plan.versions
                  .filter((version) => version.publicationState === 'published')
                  .map((version) => (
                    <MenuItem key={version.versionId} value={version.versionId}>
                      {version.title} · {version.versionNumber}
                    </MenuItem>
                  )),
              )}
          </TextField>
          {failure?.code === 'PLAN_DOES_NOT_FIT' && (
            <TextField
              size="small"
              required
              sx={{ minWidth: 300 }}
              label={translate(locale, 'training.attach.override')}
              value={planOverride}
              onChange={(changed) => {
                setPlanOverride(changed.target.value);
              }}
            />
          )}
          <Button
            type="submit"
            variant="outlined"
            sx={{ minHeight: 40 }}
            disabled={chosenPlan === (event.trainingPlanVersionId ?? '')}
          >
            {translate(locale, 'training.attach.save')}
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ width: '100%' }}>
            {translate(locale, 'training.attach.help')}
          </Typography>
        </Box>
      )}

      <Box
        component="section"
        aria-label={translate(locale, 'calendar.participants.title')}
        sx={{ mt: 3 }}
      >
        <Typography variant="subtitle1" component="h4" gutterBottom>
          {translate(locale, 'calendar.participants.title')} ({participants.length})
        </Typography>
        {participants.length === 0 && (
          <Typography color="text.secondary">
            {translate(locale, 'calendar.participants.empty')}
          </Typography>
        )}
        {participants.length > 0 && (
          <>
            <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="body2" sx={{ alignSelf: 'center' }}>
                {translate(locale, 'calendar.participants.answers')}:
              </Typography>
              {rsvpResponses.map((response) => (
                <Chip
                  key={response}
                  size="small"
                  label={`${translate(locale, rsvpKeys[response])}: ${String(counts[response])}`}
                />
              ))}
              <Chip
                size="small"
                variant="outlined"
                label={`${translate(locale, 'calendar.rsvp.none')}: ${String(counts.none)}`}
              />
            </Stack>
            <Stack component="ul" spacing={1} sx={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {participants.map((participant) => (
                <ParticipantLine
                  key={participant.participantId}
                  locale={locale}
                  participant={participant}
                  canCorrect={canRecord && event.status === 'published'}
                  onCorrect={correct}
                />
              ))}
            </Stack>
          </>
        )}
        {canManage && event.status === 'published' && (
          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              sx={{ minHeight: 44 }}
              onClick={() => {
                void previewRefresh();
              }}
            >
              {translate(locale, 'calendar.refresh.check')}
            </Button>
            {refreshPlan !== null && (
              <Box sx={{ mt: 1 }}>
                {refreshPlan.toAdd.length +
                  refreshPlan.toRemove.length +
                  refreshPlan.retained.length ===
                  0 && <Typography>{translate(locale, 'calendar.refresh.upToDate')}</Typography>}
                {(
                  [
                    ['calendar.refresh.toAdd', refreshPlan.toAdd],
                    ['calendar.refresh.toRemove', refreshPlan.toRemove],
                    ['calendar.refresh.retained', refreshPlan.retained],
                  ] as const
                ).map(
                  ([labelKey, entries]) =>
                    entries.length > 0 && (
                      <Typography key={labelKey} variant="body2">
                        <strong>{translate(locale, labelKey)}:</strong>{' '}
                        {entries.map((entry) => athleteDisplayName(entry, locale)).join(', ')}
                      </Typography>
                    ),
                )}
                {refreshPlan.toAdd.length + refreshPlan.toRemove.length > 0 && (
                  <Button
                    variant="contained"
                    sx={{ mt: 1, minHeight: 44 }}
                    onClick={() => {
                      void applyRefresh();
                    }}
                  >
                    {translate(locale, 'calendar.refresh.apply')}
                  </Button>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>

      {event.status === 'published' || event.status === 'completed' ? (
        <AttendanceSheet
          locale={locale}
          base={base}
          csrfToken={csrfToken}
          eventId={event.id}
          canRecord={canRecord}
        />
      ) : (
        <Typography color="text.secondary" sx={{ mt: 2 }}>
          {translate(locale, 'calendar.attendance.notOpen')}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        {formatCalendarDate(event.date, locale)}
      </Typography>
    </Paper>
  );
}
